import { useState } from 'react';

interface User {
  _id: string;
  username: string;
}

interface CreateTimeEntryFormProps {
  allUsers: User[];
  initialUserId?: string;
  initialStartTime?: string;
  initialEndTime?: string;
  onClose: () => void;
  onCreate: (data: {
    userId: string;
    startTime: string;
    endTime: string;
    pausedMinutes: number;
  }) => void;
}

const toUtcISOString = (localValue: string) => {
  if (!localValue) return '';
  return new Date(localValue).toISOString();
};

function CreateTimeEntryForm({
  allUsers,
  initialUserId,
  initialStartTime = '',
  initialEndTime = '',
  onClose,
  onCreate,
}: CreateTimeEntryFormProps) {
  const [userId, setUserId] = useState(initialUserId || (allUsers.length > 0 ? allUsers[0]._id : ''));
  const [startTime, setStartTime] = useState(initialStartTime);
  const [endTime, setEndTime] = useState(initialEndTime);
  const [pausedMinutes, setPausedMinutes] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!userId || !startTime || !endTime) {
      setError('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    if (new Date(startTime) > new Date(endTime)) {
      setError('Startzeit darf nicht nach Endzeit liegen.');
      return;
    }

    const confirmed = window.confirm('Willst du diesen Zeiteintrag wirklich anlegen?');
    if (!confirmed) return;

    setError('');
    setLoading(true);

    try {
      await onCreate({
        userId,
        startTime: toUtcISOString(startTime),
        endTime: toUtcISOString(endTime),
        pausedMinutes,
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Erstellen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="card p-4 shadow-sm" style={{ maxWidth: 400, margin: 'auto' }}>
        <h4 className="mb-3">Neuen Zeiteintrag anlegen</h4>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Benutzer */}
        <div className="mb-2">
          <label htmlFor="user-select" className="form-label">Benutzer*</label>
          <select
            id="user-select"
            className="form-select"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={loading}
          >
            {allUsers.map((u) => (
              <option key={u._id} value={u._id}>{u.username}</option>
            ))}
          </select>
        </div>

        {/* Startzeit */}
        <div className="mb-2">
          <label htmlFor="start-time" className="form-label">Startzeit*</label>
          <input
            id="start-time"
            type="datetime-local"
            className="form-control"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Endzeit */}
        <div className="mb-2">
          <label htmlFor="end-time" className="form-label">Endzeit*</label>
          <input
            id="end-time"
            type="datetime-local"
            className="form-control"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            disabled={loading}
          />
        </div>

        {/* Pausenminuten */}
        <div className="mb-3">
          <label htmlFor="paused-minutes" className="form-label">Pausenminuten</label>
          <input
            id="paused-minutes"
            type="number"
            className="form-control"
            min={0}
            value={pausedMinutes}
            onChange={(e) => setPausedMinutes(parseInt(e.target.value) || 0)}
            disabled={loading}
          />
        </div>

        <div className="d-flex justify-content-between">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Abbrechen
          </button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={loading}>
            {loading ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateTimeEntryForm;
